import { FunctionComponent, useState } from "react";
import { Contact } from "@type/core/dashboard/dashboard.types";
import { Office } from "@type/core/offices/offices.types";
import InfoModal from "@components/core/modals/InfoModal";
import { Checkbox } from "antd";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import { useTranslation } from "react-i18next";
import Confidentiality from "@components/core/subComponents/Confidentiality";
import BasicButton from "@components/core/buttons/BasicButton";
import { TransactionDetail } from "@/type/core/transaction/transaction.types";
import { ActiveUser } from "@type/core/auth/auth.types";
import {
  requestGetTransactionDetail,
  requestUpdateTransactionConfidentiality,
} from "@state/transactions/TransactionEffects";
import { toastError } from "@utils/helpers/toast-helper";

interface TransactionConfidentialitySectionProps {
  office: Office;
  transactionId?: string;
  transaction: TransactionDetail;
  authorizedExperts: Contact[];
  isAuthorized: boolean;
  members: Contact[];
  user?: ActiveUser;
  setLoading: (loading: boolean) => void;
  className?: string;
}

const TransactionConfidentialitySection: FunctionComponent<
  TransactionConfidentialitySectionProps
> = ({
  office,
  transactionId,
  transaction,
  authorizedExperts,
  isAuthorized,
  members,
  user,
  setLoading,
  className = "",
}) => {
  const { t } = useTranslation();
  const [confidential, setConfidential] = useState<boolean>(
    transaction.confidential ?? false,
  );
  const [isAddConfidentialityModalOpen, setIsAddConfidentialityModalOpen] =
    useState<boolean>(false);
  const [currentAuthorizedExperts, setCurrentAuthorizedExperts] =
    useState<Contact[]>(authorizedExperts);

  const hasUserProfileToModifyConfidentiality = (): boolean => {
    if (transaction.status === "draft") {
      return false;
    } else if (
      transaction.status === "prepared" &&
      transaction.type === "signature"
    ) {
      return (
        (office.rights &&
          (office.rights.includes("SIGNATURE_BROUILLON_PREPARER") ||
            office.rights.includes("SIGNATURE_COLLECTE_LANCER"))) ||
        false
      );
    } else if (
      transaction.status === "prepared" &&
      transaction.type === "scellement"
    ) {
      return (
        (office.rights &&
          (office.rights.includes("SCELLEMENT_BROUILLON_PREPARER") ||
            office.rights.includes("SCELLEMENT_COLLECTE_LANCER"))) ||
        false
      );
    } else if (
      transaction.status === "inProgress" &&
      transaction.type === "signature"
    ) {
      return (
        (office.rights &&
          office.rights.includes("SIGNATURE_COLLECTE_LANCER")) ||
        false
      );
    } else if (
      transaction.status === "inProgress" &&
      transaction.type === "scellement"
    ) {
      return (
        (office.rights &&
          office.rights.includes("SCELLEMENT_COLLECTE_LANCER")) ||
        false
      );
    } else if (
      (transaction.status === "finished" || transaction.status === "refused") &&
      transaction.type === "signature"
    ) {
      return (
        (office.rights &&
          office.rights.includes("SIGNATURE_COLLECTE_LANCER")) ||
        false
      );
    } else if (
      (transaction.status === "finished" || transaction.status === "refused") &&
      transaction.type === "scellement"
    ) {
      return (
        (office.rights &&
          office.rights.includes("SCELLEMENT_COLLECTE_LANCER")) ||
        false
      );
    } else {
      return false;
    }
  };

  const isDisabledCheckbox = (): boolean => {
    if (transaction.status === "draft") {
      return true;
    } else if (
      transaction.status === "prepared" ||
      transaction.status === "inProgress" ||
      transaction.status === "finished"
    ) {
      return (
        !(office.expertComptable || hasUserProfileToModifyConfidentiality()) ||
        confidential
      );
    } else {
      return false;
    }
  };

  const isVisible = (): boolean => {
    if (office.isConfidentialityOnlyEditableByCreatorAndEc) {
      if (transaction.status === "draft") {
        return confidential;
      }
      return true;
    } else {
      if (transaction.status === "draft") {
        return confidential;
      }
    }
    return false;
  };

  const isAllowedToModifyConfidentiality =
    ((office.isConfidentialityOnlyEditableByCreatorAndEc &&
      office.expertComptable) ||
      (office.isConfidentialityOnlyEditableByCreatorAndEc &&
        user?.login === transaction.creationUser) ||
      !office.isConfidentialityOnlyEditableByCreatorAndEc ||
      hasUserProfileToModifyConfidentiality()) &&
    isAuthorized;
  const hasConfidentialityChanged =
    confidential !== transaction.confidential ||
    !currentAuthorizedExperts?.every((a) => {
      return authorizedExperts.some((initialExpert) => {
        return initialExpert.email === a.email;
      });
    }) ||
    currentAuthorizedExperts?.length !== authorizedExperts.length;

  const handleCancel = (): void => {
    if (
      transaction.confidential !== undefined &&
      transaction.confidential !== confidential
    ) {
      setConfidential(transaction.confidential);
    }
    setCurrentAuthorizedExperts(authorizedExperts);
  };

  const handleSave = (): void => {
    void requestUpdateTransactionConfidentiality({
      id: transactionId,
      dto: currentAuthorizedExperts.map((expert) => {
        return expert.email;
      }),
    }).then((result) => {
      if (result.ok && result.data) {
        if (transactionId) {
          setLoading(true);
          void requestGetTransactionDetail({ id: transactionId });
        }
      } else {
        toastError(
          "Erreur lors de l'update de la confidentialité de la transaction " +
            transactionId,
        );
      }
    });
  };

  const handleAddConfidentiality = (): void => {
    setConfidential(true);
    if (authorizedExperts.length === 0) {
      const contactsToAdd =
        user &&
        members.filter(
          (contact) =>
            contact.email === user.email && contact.officeId === office.id,
        );
      contactsToAdd && setCurrentAuthorizedExperts(contactsToAdd);
    }
    setIsAddConfidentialityModalOpen(false);
  };

  return (
    <div className={`app-section-transaction-confidentiality ${className}`}>
      {isVisible() && isAllowedToModifyConfidentiality && (
        <Checkbox
          disabled={isDisabledCheckbox()}
          checked={confidential}
          onChange={(e) => {
            if (e.target.checked) {
              setIsAddConfidentialityModalOpen(true);
            }
          }}
          className="mt-4 ml-24"
          data-test="input-label"
          data-alt={t("signature.accordion.confidential.checkBoxMessage")}
          data-test-for="confidentiality"
        >
          {t("signature.accordion.confidential.checkBoxMessage")}
          <InfoModal value="hideConfidential" info />
        </Checkbox>
      )}
      {isVisible() && isAllowedToModifyConfidentiality && (
        <Confidentiality
          detail
          confidential={confidential}
          authorizedExpertForConfidentiality={currentAuthorizedExperts}
          initialAuthorizedExperts={authorizedExperts}
          isAuthorized={isAllowedToModifyConfidentiality}
          clearSelectedExpertForConfidentiality={(email: string) => {
            setCurrentAuthorizedExperts(
              currentAuthorizedExperts.filter(
                (authorizedExpert) => authorizedExpert.email !== email,
              ),
            );
          }}
          addSelectedExpertForConfidentiality={(selectedExperts: Contact[]) => {
            setCurrentAuthorizedExperts(
              currentAuthorizedExperts.length > 0
                ? [...currentAuthorizedExperts, ...selectedExperts]
                : [...selectedExperts],
            );
          }}
          transactionStatus={transaction.status ?? ""}
          officeMembers={members}
          user={user}
          officeId={office.id}
        />
      )}
      {hasConfidentialityChanged && (
        <div className="d-flex align-items-center justify-content-center flex-wrap gap-8 mb-24">
          <BasicButton
            variant="secondary"
            text={t("signature.accordion.confidential.cancel")}
            onClick={() => handleCancel()}
            className="mx-16 py-8 px-30 font-extra-small"
          />
          <BasicButton
            variant="tertiary"
            text={t("signature.accordion.confidential.save")}
            onClick={() => handleSave()}
            className="mx-16 py-8 px-30 font-extra-small"
          />
        </div>
      )}
      <ConfirmModal
        visible={isAddConfidentialityModalOpen}
        onCancel={() => setIsAddConfidentialityModalOpen(false)}
        onOk={() => handleAddConfidentiality()}
        title={t("signature.accordion.confidential.addConfidentiality")}
      />
    </div>
  );
};

export default TransactionConfidentialitySection;
